import React from 'react';
import { Icon, type IconName } from './Icon.js';
import './primitives.css';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

export interface AlertProps {
  readonly tone?: AlertTone;
  readonly title?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly onClose?: () => void;
  readonly closeLabel?: string;
  readonly icon?: boolean;
  readonly className?: string;
  readonly 'data-testid'?: string;
}

const TONE_ICON: Readonly<Record<AlertTone, IconName>> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'error',
  neutral: 'info',
};

/** Inline, token-driven alert. Severity maps to semantic colour + icon. */
export function Alert({
  tone = 'info',
  title,
  children,
  onClose,
  closeLabel = 'Dismiss',
  icon = true,
  className,
  ...rest
}: AlertProps): React.ReactElement {
  const role = tone === 'danger' || tone === 'warning' ? 'alert' : 'status';
  return (
    <div
      role={role}
      className={['tl-alert', `tl-alert--${tone}`, className ?? ''].filter(Boolean).join(' ')}
      {...rest}
    >
      {icon && <Icon name={TONE_ICON[tone]} size={18} className="tl-alert__icon" />}
      <div className="tl-alert__body">
        {title && <p className="tl-alert__title">{title}</p>}
        <p className="tl-alert__msg">{children}</p>
      </div>
      {onClose && (
        <button type="button" className="tl-alert__close" aria-label={closeLabel} onClick={onClose}>
          <Icon name="close" size={16} />
        </button>
      )}
    </div>
  );
}
